import { useEffect, useRef, useState } from "react";
import { Layers, Download, FileText, Presentation, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Steering committee pack: one content model → PDF (the record) + PPTX (the editable deck). Charts and RAG included.
// Section choice persists per change. Never includes individual stakeholder names, assessments or interview material.
const RAG_CLASS = { red: "text-danger-700", amber: "text-warning-700", green: "text-success-700", grey: "text-muted-foreground" };
const RAG_DOT = { red: "●", amber: "◆", green: "✔", grey: "○" };
const RAG_WORD = { red: "Red", amber: "Amber", green: "Green", grey: "Not measured" };
const FMT_LABEL = { pdf: "PDF — the record (final, filed with the minutes)", pptx: "PPTX — the working deck (editable, reorder, add your slides)" };

export default function CommitteePackDialog({ change, open, onClose }) {
  const [p, setP] = useState(null);
  const [sel, setSel] = useState([]);
  const [fmts, setFmts] = useState(["pdf", "pptx"]);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const poll = useRef(null);

  useEffect(() => {
    if (!open) return;
    setP(null); setJob(null); setBusy(false);
    api.get(`/changes/${change.id}/pack/preview`).then((r) => { setP(r.data); setSel(r.data.selected); setFmts(r.data.selected_formats); }).catch((e) => toast.error(errMsg(e)));
    return () => { if (poll.current) clearInterval(poll.current); };
  }, [open, change.id]);

  const download = async (jobId, fmt) => {
    const r = await api.get(`/pack/jobs/${jobId}/download/${fmt}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data); const a = document.createElement("a");
    a.href = url; a.download = `${change.ref}-steering-pack.${fmt}`; a.click(); URL.revokeObjectURL(url);
  };

  const gen = async () => {
    setBusy(true); setJob({ status: "queued", progress: 0, step: "Queued" });
    try {
      const { data } = await api.post(`/changes/${change.id}/pack`, { sections: sel, formats: fmts });
      poll.current = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/pack/jobs/${data.job_id}`);
          setJob(j);
          if (j.status === "done") {
            clearInterval(poll.current);
            for (const fmt of j.formats) await download(data.job_id, fmt);
            toast.success(`Pack ready — ${j.formats.map((f) => f.toUpperCase()).join(" + ")} downloaded`);
            setBusy(false); onClose();
          } else if (j.status === "error") {
            clearInterval(poll.current); toast.error(j.error || "Generation failed"); setBusy(false);
          }
        } catch (e) { clearInterval(poll.current); toast.error(errMsg(e)); setBusy(false); }
      }, 1200);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); setJob(null); }
  };

  const toggleSec = (k) => setSel((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);
  const toggleFmt = (f) => setFmts((s) => s.includes(f) ? s.filter((x) => x !== f) : [...s, f]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pack-dialog">
        <DialogHeader><DialogTitle className="font-head text-2xl font-extrabold flex items-center gap-2"><Layers size={18} /> Steering committee pack</DialogTitle></DialogHeader>
        {!p ? <p className="text-sm text-muted-foreground">Preparing preview…</p> : busy ? (
          <div className="space-y-3 py-6 text-sm" data-testid="pack-progress">
            <div className="flex items-center gap-2 font-semibold text-foreground"><Loader2 size={16} className="animate-spin" /> {job?.step || "Working"}…</div>
            <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-accent-500 transition-[width] duration-500" style={{ width: `${job?.progress || 5}%` }} data-testid="pack-progress-bar" /></div>
            <p className="text-xs text-muted-foreground">Charts and the editable deck take a moment — both formats come from one content model, so the numbers match.</p>
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="rounded-xl bg-muted p-3" data-testid="pack-rag-preview">
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">RAG summary · headline <span className={RAG_CLASS[p.scorecard?.headline] + " font-bold"}>{RAG_WORD[p.scorecard?.headline]}</span></div>
              <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1">
                {p.sections.find((s) => s.key === "rag_summary")?.rag?.map((ln, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`pack-rag-line-${i}`}>
                    <span className={RAG_CLASS[ln.status]} aria-hidden="true">{RAG_DOT[ln.status]}</span>
                    <span className="flex-1 text-foreground truncate">{ln.label}</span>
                    <span className={`font-bold ${RAG_CLASS[ln.status]}`}>{RAG_WORD[ln.status]}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Sections</div>
              <div className="grid sm:grid-cols-2 gap-2" role="group" aria-label="Sections to include">
                {p.sections_available.map((s) => (
                  <label key={s.key} className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${sel.includes(s.key) ? "border-ink" : "border-border"} ${s.always ? "opacity-70" : ""}`}>
                    <input type="checkbox" data-testid={`pack-section-${s.key}`} checked={sel.includes(s.key)} disabled={s.always} onChange={() => toggleSec(s.key)} className="h-4 w-4" />
                    <span className="flex-1 font-semibold text-foreground">{s.label}</span>{s.always && <span className="text-[10px] text-muted-foreground">always</span>}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Formats</div>
              <div className="space-y-2">
                {["pdf", "pptx"].map((f) => (
                  <label key={f} className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${fmts.includes(f) ? "border-ink" : "border-border"}`}>
                    <input type="checkbox" data-testid={`pack-format-${f}`} checked={fmts.includes(f)} onChange={() => toggleFmt(f)} className="h-4 w-4" />
                    {f === "pdf" ? <FileText size={15} /> : <Presentation size={15} />}
                    <span className="flex-1 font-semibold text-foreground">{FMT_LABEL[f]}</span>
                  </label>
                ))}
              </div>
            </div>

            <p className="text-xs text-muted-foreground" data-testid="pack-branding-note">Branded as <span className="font-bold">{p.branding}</span> · Powered by ChangeOS on every page · never includes: {p.excluded.join(", ").toLowerCase()}.</p>
            <div className="flex justify-end"><button type="button" data-testid="pack-generate" onClick={gen} disabled={!sel.length || !fmts.length} className="pp-btn-dark inline-flex items-center gap-1"><Download size={14} /> Generate {fmts.map((f) => f.toUpperCase()).join(" + ")}</button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
